import React from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import {
  FlexGrid,
  Text,
  CTA,
  theme,
  Heading,
  formatCurrency,
  useResponsive,
} from "@clearabee/ui-library-native";
import { Input } from "components/Core/Input/Input";
import { Image } from "components/Image/Image";
import placeholderImage from "lib/images/placeholder.png";
import { FlatCatalogue } from "@clearabee/ui-sdk";
import { useFormContext } from "lib/hooks/useFormContext";
import { ManVanCard } from "components/Cards/ManVanCard";
import { Spinner } from "components/Core/Spinner/Spinner";

interface ProductListProps {
  products: FlatCatalogue["items"];
  postcode: string | undefined;
  isLoading: boolean;
}

export const ProductList = ({
  products,
  postcode,
  isLoading,
}: ProductListProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { Field } = useFormContext();
  const { screens } = useResponsive();

  const isExtended = products.length >= 5;
  const extension =
    (screens.large && !isExtended) || (screens.xlarge && isExtended);

  return (
    <View style={{ paddingVertical: theme.spacing.medium }}>
      <FlexGrid
        cellSpacing="xsmall"
        rowSpacing="xsmall"
        wrap={extension ? "nowrap" : "wrap"}
        style={{
          justifyContent: "center",
        }}
      >
        {products
          ?.sort((a, b) => a.title.localeCompare(b.title))
          .map((product) => (
            <FlexGrid.Cell
              key={product.sku}
              base="50%"
              medium="33.33%"
              large="25%"
            >
              <CTA
                styles={{ flex: 1 }}
                shadow={Platform.OS === "web"}
                heading={
                  <Heading
                    color="brand"
                    level={4}
                    styles={{
                      marginHorizontal: "auto",
                      textAlign: "center",
                    }}
                  >
                    {product.title}
                  </Heading>
                }
                subheading={
                  product.price &&
                  !isLoading && (
                    <View
                      style={{ marginHorizontal: "auto", marginTop: "auto" }}
                    >
                      <Text
                        styles={{
                          color: theme.colors.dark.base,
                          fontFamily: "ProximaBold",
                          textAlign: "center",
                        }}
                      >
                        {!postcode && <Text>{translate("common.from")} </Text>}
                        {formatCurrency(product.price)}

                        {Platform.OS === "web" && (
                          <Text styles={{ textAlign: "center" }}>
                            {" "}
                            {translate("common.incVat")}
                          </Text>
                        )}
                      </Text>

                      {Platform.OS !== "web" && (
                        <Text styles={{ textAlign: "center" }}>
                          {translate("common.incVat")}
                        </Text>
                      )}
                    </View>
                  )
                }
                orientation="portrait"
                textColor="brand"
                background="light"
                image={
                  <Image
                    source={product.img ?? placeholderImage}
                    resizeMode="contain"
                    style={{
                      width: "90%",
                      height: "90%",
                    }}
                  />
                }
              >
                <View
                  style={{
                    marginHorizontal: "auto",
                    marginTop: "auto",
                    marginVertical: theme.spacing.xsmall,
                    minHeight: theme.spacing.xlarge,
                  }}
                >
                  {isLoading && <Spinner size="large" color="brand" />}
                  {product.price && !isLoading && (
                    <Field name={`products.${product.sku}`}>
                      {({ field }) => (
                        <Input.Quantity {...field} disabled={!postcode} />
                      )}
                    </Field>
                  )}
                  {!product.price && !isLoading && (
                    <Text fontSize="xsmall" styles={{ textAlign: "center" }}>
                      {translate("common.notAvailable")}
                    </Text>
                  )}
                </View>
              </CTA>
            </FlexGrid.Cell>
          ))}

        <FlexGrid.Cell base="100%" medium="33.33%" large="25%">
          <ManVanCard />
        </FlexGrid.Cell>
      </FlexGrid>
    </View>
  );
};
